import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Coffee, Home, Zap, CheckCircle2, AlertCircle, Clock, Heart, ArrowRight, Menu } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-6 md:px-8 max-w-4xl mx-auto ${className}`} data-id="element-1">
    {children}
  </section>;
const Highlight = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="text-amber-400 font-semibold italic" data-id="element-2">{children}</span>;
const BlockCard = ({
  icon: Icon,
  title,
  desc,
  color
}: {
  icon: any;
  title: string;
  desc: string;
  color: string;
}) => <motion.div variants={fadeInUp} className="bg-slate-900/50 border border-slate-700/50 p-6 rounded-xl backdrop-blur-sm hover:border-amber-500/30 transition-colors duration-300" data-id="element-3">
    <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center mb-4`} data-id="element-4">
      <Icon className="w-6 h-6 text-slate-900" data-id="element-5" />
    </div>
    <h3 className="text-xl font-serif font-bold text-amber-100 mb-2" data-id="element-6">
      {title}
    </h3>
    <p className="text-slate-300 text-sm leading-relaxed" data-id="element-7">{desc}</p>
  </motion.div>;
const buyers = ['María G.', 'Ana L.', 'Sofía R.', 'Lupita M.', 'Karla V.', 'Daniela P.', 'Fernanda S.', 'Gabriela T.', 'Alejandra H.', 'Paola C.', 'Valeria N.', 'Jessica D.', 'Mónica F.', 'Laura B.', 'Claudia E.'];
const cities = ['CDMX', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana', 'León', 'Mérida', 'Querétaro', 'Cancún', 'Toluca'];
function SocialProofNotification() {
  const [visible, setVisible] = useState(false);
  const [currentBuyer, setCurrentBuyer] = useState(0);
  const [timeAgo, setTimeAgo] = useState('');
  useEffect(() => {
    const times = ['hace 2 min', 'hace 5 min', 'hace 12 min', 'hace 18 min', 'hace 23 min', 'hace 31 min', 'hace 45 min', 'hace 1 hora'];
    // Initial delay before first notification
    const initialDelay = setTimeout(() => {
      setTimeAgo(times[Math.floor(Math.random() * times.length)]);
      setVisible(true);
    }, 4000);
    // Cycle notifications
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentBuyer(prev => (prev + 1) % buyers.length);
        setTimeAgo(times[Math.floor(Math.random() * times.length)]);
        setVisible(true);
      }, 1500);
    }, 7000);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, []);
  const city = cities[currentBuyer % cities.length];
  return <div className="fixed bottom-4 left-4 z-50 max-w-sm" data-id="element-8">
      <AnimatePresence data-id="element-9">
        {visible && <motion.div initial={{
        opacity: 0,
        y: 80,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="flex items-center gap-3 bg-white/95 backdrop-blur-xl rounded-2xl p-3 pr-5 shadow-2xl shadow-black/30 border border-white/20" data-id="element-10">
            {/* Product image */}
            <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 shadow-md" data-id="element-11">
              <img src="https://cdn.magicpatterns.com/uploads/nbCkp3fRaDvADdHHBtvHBC/Pop_Notification.png" alt="Tu Quincena en Control" className="w-full h-full object-cover" data-id="element-12" />
            </div>

            {/* Content */}
            <div className="min-w-0" data-id="element-13">
              <p className="text-slate-900 text-sm font-semibold leading-tight truncate" data-id="element-14">
                {buyers[currentBuyer]}
              </p>
              <p className="text-slate-600 text-xs leading-tight mt-0.5" data-id="element-15">
                compró{' '}
                <span className="font-semibold text-slate-800" data-id="element-16">
                  Tu Quincena en Control
                </span>
              </p>
              <div className="flex items-center gap-2 mt-1" data-id="element-17">
                <span className="text-emerald-600 text-xs font-bold" data-id="element-18">
                  $9.97 USD
                </span>
                <span className="text-slate-400 text-[10px]" data-id="element-19">•</span>
                <span className="text-slate-400 text-[10px]" data-id="element-20">{city}</span>
                <span className="text-slate-400 text-[10px]" data-id="element-21">•</span>
                <span className="text-slate-400 text-[10px]" data-id="element-22">{timeAgo}</span>
              </div>
            </div>

            {/* Close button */}
            <button onClick={() => setVisible(false)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-slate-200 hover:bg-slate-300 rounded-full flex items-center justify-center text-slate-500 text-xs transition-colors" data-id="element-23">
              ✕
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function SalesPage() {
  return <div className="min-h-screen bg-slate-950 text-slate-200 overflow-x-hidden selection:bg-amber-500/30" data-id="element-24">
      {/* Social Proof Notification */}
      <SocialProofNotification data-id="element-25" />

      {/* 1. HERO SECTION */}
      <header className="relative pt-8 pb-16 md:pt-12 md:pb-24 px-4 overflow-hidden" data-id="element-26">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-800/40 via-slate-950 to-slate-950 z-0" data-id="element-27" />

        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="relative z-10 max-w-5xl mx-auto text-center" data-id="element-28">
          <motion.div variants={fadeInUp} className="mb-8 md:mb-12 flex justify-center" data-id="element-29">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-amber-900/20 border border-amber-500/20 max-w-md md:max-w-lg w-full" data-id="element-30">
              <img src="https://cdn.magicpatterns.com/uploads/ee6VgEpqqMJxofGSXYLtSJ/Cover.png" alt="Tu Quincena en Control Cover" className="w-full h-auto" data-id="element-31" />
            </div>
          </motion.div>

          <motion.h2 variants={fadeInUp} className="text-xl md:text-3xl font-serif italic text-slate-300 max-w-3xl mx-auto leading-relaxed" data-id="element-32">
            Sin fuerza de voluntad.{' '}
            <span className="text-amber-400" data-id="element-33">Sin perfección.</span>{' '}
            <br className="hidden md:block" data-id="element-34" />
            Solo un sistema que funciona con{' '}
            <span className="text-amber-400" data-id="element-35">tu vida real.</span>
          </motion.h2>
        </motion.div>
      </header>

      {/* 2. OPENING HOOK */}
      <Section className="text-lg md:text-xl leading-relaxed space-y-8" data-id="element-36">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} data-id="element-37">
          <p className="font-serif text-2xl md:text-3xl text-amber-100 mb-8" data-id="element-38">
            Te voy a decir algo que probablemente nadie te ha dicho de frente:
          </p>
          <p className="text-2xl md:text-3xl font-bold text-white mb-8 border-l-4 border-amber-500 pl-6" data-id="element-39">
            No estás desordenada… estás saturada.
          </p>
          <p data-id="element-40">
            Y cuando estás saturada, tu dinero no se "gasta"…{' '}
            <Highlight data-id="element-41">tu dinero se fuga.</Highlight>
          </p>
          <p data-id="element-42">
            Se fuga en recargas. Se fuga en Uber porque ya no te da la cabeza
            para "ver cómo le haces". Se fuga en "nomás tantito" en OXXO. Se
            fuga en snacks, antojitos, la vuelta rápida, el cafecito para
            sobrevivir el día. Se fuga en "me lo merezco" porque estás cargando
            demasiado.
          </p>
        </motion.div>
      </Section>

      {/* 3. PAIN POINTS SECTION */}
      <Section className="bg-slate-900/30 rounded-3xl border border-slate-800" data-id="element-43">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="space-y-8" data-id="element-44">
          <motion.h3 variants={fadeInUp} className="text-2xl md:text-3xl font-serif text-amber-100" data-id="element-45">
            Y luego pasa lo que ya conoces:
          </motion.h3>

          <motion.p variants={fadeInUp} className="text-lg" data-id="element-46">
            Cobras… y sin darte cuenta, antes de respirar ya se fue.
          </motion.p>

          <motion.div variants={fadeInUp} className="space-y-4" data-id="element-47">
            <p className="text-lg" data-id="element-48">
              No porque seas mala con el dinero. No porque "no tengas
              disciplina". Y definitivamente no porque no seas capaz.
            </p>
            <p className="text-lg" data-id="element-49">
              Pasa porque estás viviendo en un modo que muchas mujeres conocen
              demasiado bien:
            </p>
            <div className="bg-slate-950 p-8 rounded-xl border border-amber-900/30 shadow-inner" data-id="element-50">
              <h4 className="text-2xl font-serif text-amber-400 mb-6 text-center" data-id="element-51">
                Modo Resolver
              </h4>
              <ul className="space-y-3 text-center text-lg text-slate-300" data-id="element-52">
                <li data-id="element-53">Ese modo donde tú eres la que:</li>
                <li className="font-semibold text-white" data-id="element-54">organiza,</li>
                <li className="font-semibold text-white" data-id="element-55">sostiene,</li>
                <li className="font-semibold text-white" data-id="element-56">ayuda,</li>
                <li className="font-semibold text-white" data-id="element-57">resuelve,</li>
                <li className="italic text-amber-200/80" data-id="element-58">
                  y no puede quedar mal.
                </li>
              </ul>
            </div>
          </motion.div>

          <motion.p variants={fadeInUp} className="text-lg" data-id="element-59">
            Por fuera sigues cumpliendo. Por dentro tu cabeza no se calla.
          </motion.p>

          <motion.div variants={fadeInUp} className="bg-amber-950/20 p-6 rounded-lg border-l-4 border-amber-500" data-id="element-60">
            <p className="text-xl font-medium text-amber-100 mb-2" data-id="element-61">
              Y lo peor es esto: no es que te falte dinero.
            </p>
            <p className="text-xl text-white" data-id="element-62">
              Es que te sobra carga mental… y te falta un sistema.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-4 pt-4" data-id="element-63">
            {['“Tengo demasiadas cosas en la cabeza.”', '“Si yo no organizo todo, se cae la casa.”', '“Me da pena admitir que no sé dónde se va.”', '“Quiero sentirme adulta… pero sin castigarme.”'].map((quote, i) => <div key={i} className="bg-slate-800/50 p-4 rounded-lg italic text-slate-400 text-sm md:text-base" data-id="element-64">
                {quote}
              </div>)}
          </motion.div>

          <motion.p variants={fadeInUp} className="text-lg pt-4" data-id="element-65">
            Si tú eres de las que cobra y a los dos días ya siente la quincena
            apretada… si pasas por OXXO y siempre es "nomás tantito"… si te
            compras un gustito para aguantar la semana y luego te cae la culpa…
          </motion.p>

          <motion.p variants={fadeInUp} className="text-lg font-semibold text-white" data-id="element-66">
            esto no es porque seas mala con el dinero.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-lg" data-id="element-67">
            Es porque estás cargando demasiado… y estás administrando tu
            quincena a puro "resolver".
          </motion.p>

          <motion.div variants={fadeInUp} className="text-center pt-8" data-id="element-68">
            <p className="text-xl text-amber-200 mb-2" data-id="element-69">
              Lo que te falta no es disciplina.
            </p>
            <p className="text-2xl font-serif text-white" data-id="element-70">
              Es un sistema que funcione con tu vida real.
            </p>
            <p className="text-slate-400 mt-2" data-id="element-71">
              Uno que te dé orden YA, sin castigo, sin perfección… y con
              resultados desde la primera quincena.
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* 4. THE REPEATING SCENE */}
      <Section data-id="element-72">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="space-y-8" data-id="element-73">
          <motion.h3 variants={fadeInUp} className="text-2xl md:text-4xl font-serif text-center mb-12" data-id="element-74">
            La escena que se repite{' '}
            <span className="text-slate-500 text-xl block md:inline md:text-3xl" data-id="element-75">
              (y que nadie entiende)
            </span>
          </motion.h3>

          <motion.div variants={fadeInUp} className="space-y-6" data-id="element-76">
            <p className="text-center text-amber-400 font-medium tracking-wide uppercase text-sm" data-id="element-77">
              A ver si esto te suena:
            </p>

            <div className="space-y-4" data-id="element-78">
              {['"Cobré y antes de respirar ya se fue en renta, recargas, Uber, comida y una vuelta rápida al OXXO."', '"Me doy un gustito para no colapsar… y luego me siento culpable todo el día."', '"Me da miedo una emergencia pequeña porque sé que me descuadra todo."', '"Le digo a todos que \'estoy bien\', pero por dentro estoy contando pesos."', '"Yo puedo con todo… pero ya me cansé de improvisar."'].map((quote, i) => <div key={i} className="flex gap-4 items-start" data-id="element-79">
                  <div className="mt-1.5 min-w-[20px] h-[1px] bg-amber-500/50" data-id="element-80"></div>
                  <p className="text-slate-300 italic text-lg" data-id="element-81">{quote}</p>
                </div>)}
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="py-8 border-y border-slate-800 my-8" data-id="element-82">
            <p className="text-xl text-center mb-4" data-id="element-83">Y aquí viene lo fuerte:</p>
            <h4 className="text-3xl md:text-4xl font-serif text-center text-white mb-6" data-id="element-84">
              No te está faltando "motivación".
              <br data-id="element-85" />
              <span className="text-amber-400" data-id="element-86">
                Te está faltando estructura.
              </span>
            </h4>
            <p className="text-lg text-center max-w-2xl mx-auto" data-id="element-87">
              Porque tu quincena no se va por el gasto grande… se va por el{' '}
              <Highlight data-id="element-88">goteo de cositas.</Highlight>
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="bg-slate-900 p-6 rounded-xl" data-id="element-89">
            <p className="mb-4 text-lg" data-id="element-90">
              Y ese goteo es traicionero porque viene disfrazado de "no es
              tanto":
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-91">
              <div className="bg-slate-950 p-3 rounded text-center text-slate-400 text-sm" data-id="element-92">
                "Solo fueron $3… $5… $10…"
              </div>
              <div className="bg-slate-950 p-3 rounded text-center text-slate-400 text-sm" data-id="element-93">
                "Solo fue una recarga."
              </div>
              <div className="bg-slate-950 p-3 rounded text-center text-slate-400 text-sm" data-id="element-94">
                "Solo fue un snack."
              </div>
              <div className="bg-slate-950 p-3 rounded text-center text-slate-400 text-sm" data-id="element-95">
                "Solo fue un Uber."
              </div>
            </div>
            <p className="text-lg" data-id="element-96">
              Y cuando llega el día 7, 10 o 12… te das cuenta de que lo
              "chiquito" sumó lo suficiente para dejarte sin aire.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="text-center space-y-4" data-id="element-97">
            <p className="text-lg" data-id="element-98">
              Pero lo peor no es el dinero. Lo peor es la sensación de:
            </p>
            <p className="text-3xl font-serif font-bold text-red-400/80" data-id="element-99">
              "No tengo control."
            </p>
            <p className="text-lg" data-id="element-100">
              Y eso te pesa, porque tú eres orgullosa. Tú eres capaz. Tú no
              quieres sentirte "atrás".
            </p>
            <div className="pt-4" data-id="element-101">
              <p className="text-lg mb-2" data-id="element-102">Tú quieres sentir (de verdad):</p>
              <p className="text-2xl font-serif text-emerald-400" data-id="element-103">
                "Sí puedo… solo necesito orden."
              </p>
            </div>
          </motion.div>
        </motion.div>
      </Section>

      {/* 5. THE TRUTH SECTION */}
      <Section className="bg-gradient-to-b from-slate-900 to-slate-950 rounded-3xl border border-amber-900/20" data-id="element-104">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="text-center space-y-8" data-id="element-105">
          <motion.h3 variants={fadeInUp} className="text-3xl md:text-4xl font-serif text-amber-400 mb-8" data-id="element-106">
            Aquí está la verdad que cambia todo
          </motion.h3>

          <motion.div variants={fadeInUp} className="space-y-4" data-id="element-107">
            <p className="text-xl" data-id="element-108">
              Cuando estás saturada, lo que te destruye no es gastar…
            </p>
            <p className="text-4xl font-bold text-white tracking-widest uppercase" data-id="element-109">
              es decidir.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="bg-slate-950/50 p-6 rounded-xl border border-slate-800 inline-block text-left mx-auto" data-id="element-110">
            <p className="font-medium text-slate-400 mb-4 text-center uppercase tracking-wider text-xs" data-id="element-111">
              Decidir mil veces al día:
            </p>
            <ul className="space-y-2 text-lg text-slate-200" data-id="element-112">
              <li className="flex items-center gap-3" data-id="element-113">
                <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-114" /> "¿Lo compro o
                no?"
              </li>
              <li className="flex items-center gap-3" data-id="element-115">
                <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-116" /> "¿Me
                alcanza?"
              </li>
              <li className="flex items-center gap-3" data-id="element-117">
                <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-118" /> "¿Y si mañana
                pasa algo?"
              </li>
              <li className="flex items-center gap-3" data-id="element-119">
                <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-120" /> "¿Y si mi
                familia necesita…?"
              </li>
              <li className="flex items-center gap-3" data-id="element-121">
                <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-122" /> "¿Y si no
                debería…?"
              </li>
            </ul>
          </motion.div>

          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" data-id="element-123">
            Eso agota. Y cuando estás agotada, haces lo que cualquier humana
            hace: <br data-id="element-124" />
            <span className="text-white font-semibold" data-id="element-125">
              improvisas… y luego viene la culpa.
            </span>
          </motion.p>

          <motion.p variants={fadeInUp} className="text-lg max-w-2xl mx-auto" data-id="element-126">
            Por eso los presupuestos perfectos no te funcionan. Por eso las apps
            complicadas te duran 3 días. Por eso te prometes "ahora sí" y luego
            vuelve el caos.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-xl font-serif text-amber-100" data-id="element-127">
            No porque no puedas. Porque tu mente ya está llena.
          </motion.p>
        </motion.div>
      </Section>

      {/* 6. PRODUCT INTRODUCTION */}
      <Section id="product" data-id="element-128">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-129">
          <motion.div variants={fadeInUp} className="text-center mb-16" data-id="element-130">
            <p className="text-amber-500 font-medium tracking-widest uppercase mb-4" data-id="element-131">
              Presentando
            </p>
            <h2 className="text-5xl md:text-7xl font-serif text-white mb-6 gold-gradient-text drop-shadow-sm" data-id="element-132">
              Tu Quincena
              <br data-id="element-133" />
              en Control
            </h2>
            <p className="text-xl md:text-2xl text-slate-300 max-w-2xl mx-auto font-light" data-id="element-134">
              Sin fuerza de voluntad. Sin perfección. <br data-id="element-135" />
              <span className="text-amber-400 font-normal" data-id="element-136">
                Solo un sistema que funciona con tu vida real.
              </span>
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="mb-12 text-center max-w-2xl mx-auto" data-id="element-137">
            <p className="text-lg mb-4" data-id="element-138">Esto no es "otro presupuesto".</p>
            <p className="text-lg" data-id="element-139">
              Es el{' '}
              <strong className="text-white" data-id="element-140">
                Sistema de Bloques Quincenales
              </strong>
              : un método simple para que, apenas cobres, tu dinero se vaya a
              bloques claros.
            </p>
            <p className="text-lg mt-4" data-id="element-141">
              Bloques que reflejan tu vida real: <br data-id="element-142" />
              <span className="text-slate-400" data-id="element-143">
                tu renta, tu comida, tus recargas, tu transporte, tus
                responsabilidades… y sí: también tus gustitos.
              </span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-144">
            <BlockCard icon={Home} color="bg-blue-200" title="Bloque 1: Lo básico sí o sí" desc="(renta, comida base, transporte esencial, recibos)" data-id="element-145" />
            <BlockCard icon={Zap} color="bg-amber-200" title="Bloque 2: Vida real / 'Siempre pasa algo'" desc="(imprevistos pequeños, cosas del día a día)" data-id="element-146" />
            <BlockCard icon={Coffee} color="bg-pink-200" title="Bloque 3: Gustitos sin culpa" desc="(OXXO, antojos, cafecito, algo para ti… pero con límite claro)" data-id="element-147" />
            <BlockCard icon={ShieldCheck} color="bg-emerald-200" title="Bloque 4: Mini fondo / Blindaje" desc="(para que una emergencia de $20–$50 no te rompa)" data-id="element-148" />
          </div>

          <motion.div variants={fadeInUp} className="bg-slate-800/30 p-8 rounded-2xl border border-amber-500/20 text-center" data-id="element-149">
            <h4 className="text-2xl font-serif text-amber-400 mb-4" data-id="element-150">
              Porque el problema no es el gustito.
            </h4>
            <p className="text-xl text-white mb-6" data-id="element-151">
              El problema es el gustito sin plan.
            </p>
            <p className="text-lg text-slate-300" data-id="element-152">
              Cuando tu gustito está dentro de un bloque, deja de ser culpa.{' '}
              <br data-id="element-153" />
              <span className="text-amber-200 font-medium" data-id="element-154">
                Se convierte en autocuidado con estructura.
              </span>
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-12 space-y-6 text-center" data-id="element-155">
            <h3 className="text-3xl font-serif text-white" data-id="element-156">
              Lo que pasa cuando lo aplicas
            </h3>
            <p className="text-lg" data-id="element-157">
              Dejas de pelear con tu dinero. Dejas de adivinar. Dejas de
              castigar tu vida.
            </p>
            <p className="text-lg" data-id="element-158">Y empiezas a decir:</p>
            <div className="flex flex-wrap justify-center gap-3" data-id="element-159">
              <span className="px-4 py-2 bg-slate-800 rounded-full text-emerald-300 border border-emerald-900/50" data-id="element-160">
                "Ok. Esto está cubierto."
              </span>
              <span className="px-4 py-2 bg-slate-800 rounded-full text-pink-300 border border-pink-900/50" data-id="element-161">
                "Esto es para gustitos."
              </span>
              <span className="px-4 py-2 bg-slate-800 rounded-full text-amber-300 border border-amber-900/50" data-id="element-162">
                "Esto es para imprevistos."
              </span>
              <span className="px-4 py-2 bg-slate-800 rounded-full text-blue-300 border border-blue-900/50" data-id="element-163">
                "Esto me protege."
              </span>
            </div>
            <div className="pt-6" data-id="element-164">
              <p className="text-2xl font-serif text-white" data-id="element-165">
                Eso es control.{' '}
                <span className="text-amber-400" data-id="element-166">Eso es paz.</span>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </Section>

      {/* 7. RESULTS TIMELINE */}
      <Section className="bg-slate-900 rounded-3xl" data-id="element-167">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} data-id="element-168">
          <motion.h3 variants={fadeInUp} className="text-3xl font-serif text-center mb-10" data-id="element-169">
            Resultados rápidos{' '}
            <span className="text-slate-500 text-xl block md:inline" data-id="element-170">
              (porque tú necesitas verlo YA)
            </span>
          </motion.h3>

          <div className="space-y-8 relative before:absolute before:left-4 md:before:left-1/2 before:top-0 before:h-full before:w-0.5 before:bg-slate-800" data-id="element-171">
            {[{
            time: 'En 30 minutos',
            desc: 'vas a ver tu quincena clara (sin ansiedad).'
          }, {
            time: 'En 7 días',
            desc: 'vas a sentir menos culpa y más control.'
          }, {
            time: 'En 1 quincena',
            desc: 'vas a cubrir lo básico, tener mini fondo y gustitos sin sabotaje.'
          }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-0" data-id="element-172">
                <div className="md:w-1/2 md:pr-12 md:text-right pl-12 md:pl-0" data-id="element-173">
                  <h4 className="text-xl font-bold text-amber-400" data-id="element-174">
                    {item.time}
                  </h4>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-[5px] md:-translate-x-1/2 w-3 h-3 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]" data-id="element-175"></div>
                <div className="md:w-1/2 md:pl-12 pl-12" data-id="element-176">
                  <p className="text-lg text-slate-200" data-id="element-177">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>

          <motion.div variants={fadeInUp} className="text-center mt-12 pt-8 border-t border-slate-800" data-id="element-178">
            <p className="text-xl text-slate-400 mb-2" data-id="element-179">
              No te estoy prometiendo magia.
            </p>
            <p className="text-2xl font-serif text-white" data-id="element-180">
              Te estoy prometiendo un sistema real.
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* 8. OBJECTIONS */}
      <Section data-id="element-181">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="space-y-12" data-id="element-182">
          <motion.div variants={fadeInUp} className="text-center" data-id="element-183">
            <h3 className="text-3xl font-serif text-white mb-4" data-id="element-184">
              "Pero yo ya intenté presupuesto…"
            </h3>
            <p className="text-xl text-amber-400" data-id="element-185">
              Perfecto. Justo por eso esto te va a gustar.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-8" data-id="element-186">
            <div className="space-y-4" data-id="element-187">
              <p className="text-lg" data-id="element-188">
                Porque "Tu Quincena en Control" no depende de que seas perfecta.
              </p>
              <ul className="space-y-2 text-slate-400" data-id="element-189">
                <li className="flex items-center gap-2" data-id="element-190">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-191" /> No
                  depende de que registres cada gasto.
                </li>
                <li className="flex items-center gap-2" data-id="element-192">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-193" /> No
                  depende de que no falles.
                </li>
                <li className="flex items-center gap-2" data-id="element-194">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-195" /> No
                  depende de que tengas tiempo.
                </li>
              </ul>
            </div>
            <div className="bg-slate-900 p-6 rounded-xl border border-slate-800" data-id="element-196">
              <p className="text-lg mb-4" data-id="element-197">
                Depende de algo simple:{' '}
                <span className="text-amber-400 font-bold text-xl" data-id="element-198">
                  bloques.
                </span>
              </p>
              <p className="text-sm text-slate-400 mb-4" data-id="element-199">
                Bloques diseñados para una vida donde:
              </p>
              <ul className="space-y-1 text-slate-300" data-id="element-200">
                <li className="flex items-center gap-2" data-id="element-201">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-202" /> siempre
                  pasa algo,
                </li>
                <li className="flex items-center gap-2" data-id="element-203">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-204" /> la
                  familia pide,
                </li>
                <li className="flex items-center gap-2" data-id="element-205">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-206" /> el
                  cansancio gana,
                </li>
                <li className="flex items-center gap-2" data-id="element-207">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-208" /> y OXXO
                  está en cada esquina.
                </li>
              </ul>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="text-center" data-id="element-209">
            <p className="text-lg" data-id="element-210">No es que gastes "mal".</p>
            <p className="text-xl font-serif text-white" data-id="element-211">
              Es que tu vida real necesita un método diseñado para eso.
            </p>
          </motion.div>

          {/* How it works simple explanation */}
          <motion.div variants={fadeInUp} className="bg-slate-800/20 p-8 rounded-2xl" data-id="element-212">
            <h3 className="text-2xl font-serif text-center mb-8" data-id="element-213">
              ¿Cómo funciona? (sin complicarte)
            </h3>
            <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-214">
              <div className="text-center md:text-right space-y-2 opacity-60" data-id="element-215">
                <p className="font-serif text-xl" data-id="element-216">Antes</p>
                <p data-id="element-217">Imagina que tu quincena es una mesa.</p>
                <p data-id="element-218">Tú ponías todo encima y esperabas que alcanzara.</p>
              </div>
              <div className="text-center md:text-left space-y-2" data-id="element-219">
                <p className="font-serif text-xl text-amber-400" data-id="element-220">Ahora</p>
                <p data-id="element-221">Con el Sistema de Bloques, tú separas en cajitas claras.</p>
                <p className="text-emerald-300" data-id="element-222">
                  Y de pronto ocurre algo que no tiene precio:
                </p>
                <p className="font-bold text-white" data-id="element-223">tu cabeza descansa.</p>
              </div>
            </div>
            <div className="mt-8 text-center space-y-2" data-id="element-224">
              <p data-id="element-225">
                Porque ya no decides todo el día. Ya no adivinas. Ya no te
                castigas.
              </p>
              <p className="text-xl font-serif text-amber-200" data-id="element-226">
                Solo sigues el sistema.
              </p>
            </div>
          </motion.div>

          {/* Objections List */}
          <motion.div variants={fadeInUp} className="space-y-6" data-id="element-227">
            <h3 className="text-2xl font-serif text-center mb-6" data-id="element-228">
              Objeciones reales (y respuestas reales)
            </h3>
            <div className="grid md:grid-cols-2 gap-4" data-id="element-229">
              <div className="bg-slate-900 p-5 rounded-lg" data-id="element-230">
                <p className="font-bold text-white mb-2" data-id="element-231">"Gano poco."</p>
                <p className="text-slate-400 text-sm" data-id="element-232">
                  Justo por eso: un sistema evita fugas y emergencias repetidas.
                </p>
              </div>
              <div className="bg-slate-900 p-5 rounded-lg" data-id="element-233">
                <p className="font-bold text-white mb-2" data-id="element-234">"No tengo tiempo."</p>
                <p className="text-slate-400 text-sm" data-id="element-235">
                  Porque estás cargada mentalmente. Esto te quita decisiones.
                </p>
              </div>
              <div className="bg-slate-900 p-5 rounded-lg" data-id="element-236">
                <p className="font-bold text-white mb-2" data-id="element-237">
                  "Siempre pasa algo."
                </p>
                <p className="text-slate-400 text-sm" data-id="element-238">
                  Por eso existen los mini fondos de vida real.
                </p>
              </div>
              <div className="bg-slate-900 p-5 rounded-lg" data-id="element-239">
                <p className="font-bold text-white mb-2" data-id="element-240">
                  "Me cuesta decirle que no a mi familia."
                </p>
                <p className="text-slate-400 text-sm" data-id="element-241">
                  Aquí hay reglas para ayudar sin quebrarte ni quedar mal.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </Section>

      {/* 9. TRANSFORMATION */}
      <Section className="bg-gradient-to-br from-amber-950/30 to-slate-950 rounded-3xl border border-amber-900/30" data-id="element-242">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="text-center" data-id="element-243">
          <motion.h3 variants={fadeInUp} className="text-3xl font-serif text-white mb-8" data-id="element-244">
            Lo que vas a sentir cuando esto funcione
          </motion.h3>

          <motion.p variants={fadeInUp} className="text-lg mb-8 text-amber-200" data-id="element-245">
            No es solo "ahorrar". Es algo más profundo:
          </motion.p>

          <motion.div variants={fadeInUp} className="grid gap-4 max-w-lg mx-auto text-left" data-id="element-246">
            {['Te despiertas con menos ansiedad.', 'Dejas de sentir culpa por cada gustito.', 'Te vuelves más clara: "esto sí, esto no".', 'Tu quincena deja de controlarte.'].map((item, i) => <div key={i} className="flex items-center gap-3 bg-slate-900/50 p-4 rounded-lg" data-id="element-247">
                <Heart className="w-5 h-5 text-amber-500 fill-amber-500/20" data-id="element-248" />
                <span className="text-slate-200" data-id="element-249">{item}</span>
              </div>)}
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-10" data-id="element-250">
            <p className="text-lg mb-2" data-id="element-251">
              Te conviertes en esa versión de ti que tú sabes que eres:
            </p>
            <p className="text-2xl font-serif text-white max-w-xl mx-auto" data-id="element-252">
              Una mujer que sí puede… pero con estructura, no con fuerza de
              voluntad.
            </p>
          </motion.div>
        </motion.div>
      </Section>

      {/* 10. QUALIFICATION */}
      <Section data-id="element-253">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-slate-900 border border-slate-800 p-8 md:p-12 rounded-2xl max-w-3xl mx-auto" data-id="element-254">
          <h3 className="text-2xl font-serif text-center text-white mb-8" data-id="element-255">
            Esto es para ti si…
          </h3>
          <ul className="space-y-4" data-id="element-256">
            {['Cobras y sientes que la quincena se te escapa.', 'Estás cansada de improvisar.', 'Te abruma "hacer presupuesto".', 'Quieres control sin dejar de vivir.', 'Te mueve el orgullo… pero ya no quieres cargar sola.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-257">
                <div className="mt-1 w-5 h-5 rounded-full border border-amber-500 flex items-center justify-center shrink-0" data-id="element-258">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" data-id="element-259"></div>
                </div>
                <span className="text-lg text-slate-300" data-id="element-260">{item}</span>
              </li>)}
          </ul>
        </motion.div>
      </Section>

      {/* 10.5 PURCHASE SECTION */}
      <Section className="relative" id="comprar" data-id="element-261">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="relative" data-id="element-262">
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-amber-500/10 to-amber-500/5 rounded-3xl blur-3xl" data-id="element-263" />

          <div className="relative bg-gradient-to-b from-slate-900 via-slate-900/95 to-slate-950 rounded-3xl border border-amber-500/30 overflow-hidden" data-id="element-264">
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400" data-id="element-265" />

            <div className="p-8 md:p-12" data-id="element-266">
              <motion.div variants={fadeInUp} className="text-center mb-8" data-id="element-267">
                <p className="text-amber-400 font-medium tracking-widest uppercase text-sm mb-3" data-id="element-268">
                  Obtén acceso inmediato
                </p>
                <h2 className="text-3xl md:text-5xl font-serif text-white mb-4" data-id="element-269">
                  Tu Quincena en Control
                </h2>
                <p className="text-slate-400 text-lg max-w-xl mx-auto" data-id="element-270">
                  El sistema completo para organizar tu dinero desde la primera
                  quincena
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12" data-id="element-271">
                {/* Product mockup */}
                <div className="flex-1 w-full max-w-lg" data-id="element-272">
                  <div className="relative" data-id="element-273">
                    <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full" data-id="element-274" />
                    <img src="https://cdn.magicpatterns.com/uploads/wtaBr24uGSAjwu5KPLeg4s/Mockup.png" alt="Tu Quincena en Control - Acceso en laptop y tablet" className="relative w-full h-auto rounded-xl" data-id="element-275" />
                  </div>
                </div>

                {/* Purchase details */}
                <div className="flex-1 w-full max-w-md" data-id="element-276">
                  <div className="bg-slate-950/80 rounded-2xl p-6 md:p-8 border border-slate-800" data-id="element-277">
                    <h3 className="text-xl font-serif text-amber-100 mb-6 text-center" data-id="element-278">
                      Lo que incluye:
                    </h3>

                    <ul className="space-y-4 mb-8" data-id="element-279">
                      {['Sistema de Bloques Quincenales completo', 'Plantilla lista para usar', 'Guía paso a paso (sin complicaciones)', 'Reglas para imprevistos y familia', 'Acceso inmediato y de por vida'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-280">
                          <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" data-id="element-281" />
                          <span className="text-slate-300" data-id="element-282">{item}</span>
                        </li>)}
                    </ul>

                    {/* Free Bonus */}
                    <div className="mb-8 bg-gradient-to-br from-amber-950/40 to-slate-900 rounded-xl p-5 border border-amber-500/30 relative overflow-hidden" data-id="element-283">
                      <div className="absolute top-0 right-0 bg-amber-500 text-slate-950 text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider" data-id="element-284">
                        Gratis
                      </div>
                      <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-3" data-id="element-285">
                        🎁 Bono incluido con tu compra
                      </p>
                      <div className="mb-3 rounded-lg overflow-hidden" data-id="element-286">
                        <img src="https://cdn.magicpatterns.com/uploads/auaTwf91Pz9iPkJMgnWXch/Mockup_Bono.png" alt="Bono Gratis - Gano Poco: Cómo Hacer Bloques Cuando Lo Que Gano No Alcanza" className="w-full h-auto" data-id="element-287" />
                      </div>
                      <h4 className="text-lg font-serif text-white mb-2" data-id="element-288">
                        Gano Poco: Cómo Hacer Bloques Cuando Lo Que Gano No
                        Alcanza
                      </h4>
                      <p className="text-slate-400 text-sm leading-relaxed" data-id="element-289">
                        Un sistema realista para organizar tu quincena aunque el
                        dinero se sienta "apretado". Porque cuando tú dices "no
                        me alcanza", no es que seas mala con el dinero — el
                        problema es que nadie te ha enseñado a organizarlo con
                        lo que tienes.
                      </p>
                      <p className="text-slate-400 text-sm leading-relaxed mt-2" data-id="element-290">
                        Incluye plantillas listas para aplicar los Bloques en 10
                        minutos: quincena normal, quincena apretada y quincena
                        con deudas (imprimible y editable). Resultado: claridad
                        inmediata y menos carga mental.
                      </p>
                      <p className="text-amber-300/70 text-xs mt-3 font-medium" data-id="element-291">
                        Valor: $4.97 USD —{' '}
                        <span className="text-emerald-400" data-id="element-292">
                          Tuyo GRATIS hoy
                        </span>
                      </p>
                    </div>

                    {/* Price */}
                    <div className="text-center mb-6" data-id="element-293">
                      <p className="text-slate-500 text-sm mb-1" data-id="element-294">
                        Precio especial de lanzamiento
                      </p>
                      <div className="flex items-center justify-center gap-3" data-id="element-295">
                        <span className="text-slate-500 line-through text-2xl" data-id="element-296">
                          $19.97
                        </span>
                        <span className="text-5xl md:text-6xl font-bold text-white" data-id="element-297">
                          $9<span className="text-3xl" data-id="element-298">.97</span>
                        </span>
                        <span className="text-amber-400 text-sm font-medium" data-id="element-299">
                          USD
                        </span>
                      </div>
                      <p className="text-emerald-400 text-sm mt-2 font-medium" data-id="element-300">
                        ¡Ahorra 50% hoy!
                      </p>
                    </div>

                    {/* CTA Button */}
                    <a href="https://www.upreferr.com/cart/43810739355783:1" target="_blank" rel="noopener noreferrer" className="group w-full px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-lg rounded-full shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2" data-id="element-301">
                      <span data-id="element-302">¡Quiero mi sistema ahora!</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-303" />
                    </a>

                    <p className="text-center text-slate-500 text-xs mt-4" data-id="element-304">
                      Pago seguro • Acceso inmediato • Garantía de satisfacción
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* 11. FINAL CTA */}
      <Section className="text-center pb-24 md:pb-32" data-id="element-305">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="space-y-8" data-id="element-306">
          <motion.div variants={fadeInUp} data-id="element-307">
            <p className="text-amber-500 font-medium tracking-widest uppercase mb-4" data-id="element-308">
              La idea más importante
            </p>
            <h2 className="text-3xl md:text-5xl font-serif text-white mb-6" data-id="element-309">
              La culpa no paga cuentas. <br data-id="element-310" />
              <span className="text-amber-400" data-id="element-311">El sistema sí.</span>
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp} className="space-y-2" data-id="element-312">
            <p className="text-2xl text-slate-300" data-id="element-313">Orden no es castigo.</p>
            <p className="text-3xl font-serif text-white" data-id="element-314">Orden es paz.</p>
          </motion.div>

          <motion.div variants={fadeInUp} className="bg-gradient-to-b from-slate-900 to-slate-950 p-8 md:p-12 rounded-3xl border border-amber-500/30 max-w-2xl mx-auto shadow-2xl shadow-amber-900/10" data-id="element-315">
            <h3 className="text-3xl font-serif text-white mb-4" data-id="element-316">Empieza hoy</h3>
            <p className="text-lg text-slate-300 mb-8" data-id="element-317">
              Si hoy sientes que tu quincena te controla, agarra Tu Quincena en
              Control y en esta misma quincena pon tu dinero en bloques.
            </p>
            <p className="text-lg text-amber-100 mb-8 italic" data-id="element-318">
              No para ser perfecta. Para sentir alivio. Para dejar de
              improvisar.
            </p>

            <button className="group relative w-full md:w-auto px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold text-lg rounded-full shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 mx-auto" data-id="element-319">
              <span data-id="element-320">Haz clic aquí y empieza hoy</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-321" />
            </button>
          </motion.div>
        </motion.div>
      </Section>

      <footer className="py-12 text-center text-slate-600 text-sm" data-id="element-322">
        <p data-id="element-323">© 2024 Tu Quincena en Control. Todos los derechos reservados.</p>
      </footer>
    </div>;
}